export interface AffiliateDashboardData {
  affiliate_id: string;
  user_id: string;
  currency: string;
  referral_code: string;
  referral_link: string;
  program_name: string;
  model: string;
  revshare_bps: number;
  cpa_reward_cents: number;
  total_earnings_cents: number;
  withdrawn_cents: number;
  available_commission_cents: number;
  status: string;
}

export class AffiliatePortal {
  private container: HTMLElement;
  private token: string;
  private data: AffiliateDashboardData | null = null;

  constructor(container: HTMLElement, token: string) {
    this.container = container;
    this.token = token;
  }

  public async loadDashboard(): Promise<void> {
    if (!this.token) {
      const origin = typeof window !== 'undefined' ? window.location.origin : 'https://starxoptions.com';
      this.data = {
        affiliate_id: "demo-affiliate",
        user_id: "guest-user",
        currency: "USD",
        referral_code: "STARX-PRO",
        referral_link: `${origin}/register?ref=STARX-PRO`,
        program_name: "StarX Affiliate Partner Program",
        model: "REVENUE_SHARE",
        revshare_bps: 5000,
        cpa_reward_cents: 0,
        total_earnings_cents: 0,
        withdrawn_cents: 0,
        available_commission_cents: 0,
        status: "ACTIVE",
      };
      this.render();
      return;
    }

    try {
      const res = await fetch('/api/v1/affiliate/dashboard', {
        headers: {
          'Authorization': `Bearer ${this.token}`,
        },
      });

      if (res.ok) {
        this.data = await res.json();
      } else {
        const origin = typeof window !== 'undefined' ? window.location.origin : 'https://starxoptions.com';
        this.data = {
          affiliate_id: "demo-affiliate",
          user_id: "guest-user",
          currency: "USD",
          referral_code: "STARX-PRO",
          referral_link: `${origin}/register?ref=STARX-PRO`,
          program_name: "StarX Affiliate Partner Program",
          model: "REVENUE_SHARE",
          revshare_bps: 5000,
          cpa_reward_cents: 0,
          total_earnings_cents: 0,
          withdrawn_cents: 0,
          available_commission_cents: 0,
          status: "ACTIVE",
        };
      }
    } catch {
      const origin = typeof window !== 'undefined' ? window.location.origin : 'https://starxoptions.com';
      this.data = {
        affiliate_id: "demo-affiliate",
        user_id: "guest-user",
        currency: "USD",
        referral_code: "STARX-PRO",
        referral_link: `${origin}/register?ref=STARX-PRO`,
        program_name: "StarX Affiliate Partner Program",
        model: "REVENUE_SHARE",
        revshare_bps: 5000,
        cpa_reward_cents: 0,
        total_earnings_cents: 0,
        withdrawn_cents: 0,
        available_commission_cents: 0,
        status: "ACTIVE",
      };
    }
    this.render();
  }

  public render(): void {
    if (!this.data) {
      this.container.innerHTML = `
        <div style="background: #0f172a; border: 1px solid #1e293b; border-radius: 12px; padding: 32px; max-width: 680px; width: 100%; color: #94a3b8; text-align: center;">
          <div style="color: #38bdf8; font-size: 14px;">Loading affiliate partner dashboard...</div>
        </div>
      `;
      return;
    }

    const availUSD = (this.data.available_commission_cents / 100).toFixed(2);
    const totalUSD = (this.data.total_earnings_cents / 100).toFixed(2);
    const withUSD = (this.data.withdrawn_cents / 100).toFixed(2);
    const revsharePct = (this.data.revshare_bps / 100).toFixed(1);
    const cpaUSD = (this.data.cpa_reward_cents / 100).toFixed(2);

    this.container.innerHTML = `
      <div class="affiliate-portal" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #fff; background: #0f172a; padding: 24px; border-radius: 12px; border: 1px solid #1e293b; max-width: 760px; width: 100%; box-shadow: 0 20px 40px rgba(0,0,0,0.6); text-align: left;">
        
        <!-- Header -->
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; border-bottom: 1px solid #1e293b; padding-bottom: 16px;">
          <div>
            <div style="display: flex; align-items: center; gap: 8px;">
              <span style="font-size: 22px;">🤝</span>
              <h2 style="margin: 0; font-size: 18px; color: #f8fafc; font-weight: 700;">Partner & Referral Program Hub</h2>
            </div>
            <p style="margin: 4px 0 0 0; font-size: 12px; color: #94a3b8;">
              ${this.data.program_name} • <span style="color: #38bdf8; font-weight: 600;">${revsharePct}% RevShare + $${cpaUSD} CPA</span>
            </p>
          </div>
          <div style="display: flex; align-items: center; gap: 12px;">
            <span style="background: rgba(16, 185, 129, 0.15); color: #34d399; border: 1px solid rgba(16, 185, 129, 0.3); padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; text-transform: uppercase;">
              ● ${this.data.status}
            </span>
            <button id="close-affiliate-modal" style="background: transparent; border: none; color: #64748b; font-size: 22px; cursor: pointer; line-height: 1; padding: 0 4px;" title="Close modal">&times;</button>
          </div>
        </div>

        <!-- Referral Link & Code Box -->
        <div style="background: #131722; padding: 16px; border-radius: 8px; margin-bottom: 16px; border: 1px solid #1e293b;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
            <label style="font-size: 12px; color: #94a3b8; font-weight: 600;">Dedicated Referral Link (Cryptographically Signed Attribution)</label>
            <div style="font-size: 11px; color: #64748b;">Code: <strong id="aff-ref-code" style="color: #38bdf8; font-family: monospace;">${this.data.referral_code}</strong></div>
          </div>
          <div style="display: flex; gap: 10px;">
            <input id="aff-ref-link" type="text" readonly value="${this.data.referral_link}" style="flex: 1; padding: 10px 12px; background: #0b0e14; border: 1px solid #334155; border-radius: 6px; color: #38bdf8; font-family: monospace; font-size: 12px;" />
            <button id="btn-copy-link" style="background: linear-gradient(135deg, #0284c7, #2563eb); color: #fff; border: none; padding: 10px 18px; border-radius: 6px; cursor: pointer; font-weight: 700; font-size: 12px; white-space: nowrap;">Copy Link</button>
          </div>
        </div>

        <!-- Tiered RevShare Program Overview -->
        <div style="background: #131722; padding: 14px 16px; border-radius: 8px; margin-bottom: 20px; border: 1px solid #1e293b;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
            <div style="font-size: 12px; font-weight: 700; color: #38bdf8; display: flex; align-items: center; gap: 6px;">
              <span>🌟</span> Tiered RevShare Structure (Monthly First Time Deposits)
            </div>
            <div style="font-size: 11px; color: #94a3b8;">Current Payout: <strong style="color: #34d399;">${revsharePct}%</strong></div>
          </div>
          <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; text-align: center;">
            <div style="background: #0b0e14; border: 1px solid #1e293b; border-radius: 6px; padding: 10px 6px;">
              <div style="font-size: 10px; color: #94a3b8; font-weight: 600;">Tier 1 (1–10 FTDs)</div>
              <div style="font-size: 16px; font-weight: 800; color: #38bdf8; margin: 2px 0;">50%</div>
              <div style="font-size: 10px; color: #64748b;">Starter Partner</div>
            </div>
            <div style="background: #0b0e14; border: 1px solid #1e293b; border-radius: 6px; padding: 10px 6px;">
              <div style="font-size: 10px; color: #94a3b8; font-weight: 600;">Tier 2 (11–50 FTDs)</div>
              <div style="font-size: 16px; font-weight: 800; color: #34d399; margin: 2px 0;">60%</div>
              <div style="font-size: 10px; color: #64748b;">Pro Partner</div>
            </div>
            <div style="background: #0b0e14; border: 1px solid #1e293b; border-radius: 6px; padding: 10px 6px;">
              <div style="font-size: 10px; color: #94a3b8; font-weight: 600;">Tier 3 (51–100 FTDs)</div>
              <div style="font-size: 16px; font-weight: 800; color: #fbbf24; margin: 2px 0;">70%</div>
              <div style="font-size: 10px; color: #64748b;">Master Partner</div>
            </div>
            <div style="background: #0b0e14; border: 1px solid rgba(245, 158, 11, 0.4); border-radius: 6px; padding: 10px 6px;">
              <div style="font-size: 10px; color: #f59e0b; font-weight: 600;">Tier 4 (100+ FTDs)</div>
              <div style="font-size: 16px; font-weight: 800; color: #f59e0b; margin: 2px 0;">80%</div>
              <div style="font-size: 10px; color: #fbbf24; font-weight: 700;">VIP Partner</div>
            </div>
          </div>
        </div>

        <!-- Metrics Grid -->
        <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-bottom: 20px;">
          <div style="background: #131722; padding: 16px; border-radius: 8px; border: 1px solid #1e293b; border-left: 4px solid #10b981;">
            <div style="font-size: 11px; color: #94a3b8; font-weight: 700; text-transform: uppercase;">Available Matured Balance</div>
            <div style="font-size: 24px; font-weight: 800; color: #10b981; margin-top: 4px; font-family: monospace;">$${availUSD}</div>
            <div style="font-size: 11px; color: #34d399; margin-top: 2px;">Ready to claim & withdraw</div>
          </div>

          <div style="background: #131722; padding: 16px; border-radius: 8px; border: 1px solid #1e293b; border-left: 4px solid #38bdf8;">
            <div style="font-size: 11px; color: #94a3b8; font-weight: 700; text-transform: uppercase;">Total Earned</div>
            <div style="font-size: 24px; font-weight: 800; color: #38bdf8; margin-top: 4px; font-family: monospace;">$${totalUSD}</div>
            <div style="font-size: 11px; color: #7dd3fc; margin-top: 2px;">Cumulative commission</div>
          </div>

          <div style="background: #131722; padding: 16px; border-radius: 8px; border: 1px solid #1e293b; border-left: 4px solid #f59e0b;">
            <div style="font-size: 11px; color: #94a3b8; font-weight: 700; text-transform: uppercase;">Total Withdrawn</div>
            <div style="font-size: 24px; font-weight: 800; color: #f59e0b; margin-top: 4px; font-family: monospace;">$${withUSD}</div>
            <div style="font-size: 11px; color: #fde68a; margin-top: 2px;">Audited disbursements</div>
          </div>
        </div>

        <!-- Payout Request Form -->
        <div style="background: #131722; padding: 20px; border-radius: 8px; border: 1px solid #1e293b;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
            <h3 style="margin: 0; font-size: 15px; color: #f1f5f9; font-weight: 700;">⚡ Claim &amp; Withdraw Accumulated Commission</h3>
            <span style="font-size: 11px; color: #94a3b8;">Min: $10.00 • Zero processing fee</span>
          </div>

          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px;">
            <div>
              <label style="display: block; font-size: 11px; color: #94a3b8; margin-bottom: 4px; font-weight: 600;">Payout Amount (USD)</label>
              <div style="position: relative;">
                <input id="payout-amount" type="number" step="1" min="10" max="${availUSD}" value="${parseFloat(availUSD) > 0 ? availUSD : '0.00'}" placeholder="0.00" style="width: 100%; box-sizing: border-box; padding: 10px 12px; background: #0b0e14; border: 1px solid #334155; border-radius: 6px; color: #fff; font-family: monospace; font-size: 14px; font-weight: 700;" />
              </div>
              <div style="display: flex; gap: 6px; margin-top: 6px;">
                <button type="button" class="btn-payout-pct" data-pct="25" style="background: #1e293b; border: 1px solid #334155; color: #94a3b8; padding: 3px 8px; border-radius: 4px; font-size: 10px; cursor: pointer;">25%</button>
                <button type="button" class="btn-payout-pct" data-pct="50" style="background: #1e293b; border: 1px solid #334155; color: #94a3b8; padding: 3px 8px; border-radius: 4px; font-size: 10px; cursor: pointer;">50%</button>
                <button type="button" class="btn-payout-pct" data-pct="100" style="background: #1e293b; border: 1px solid #38bdf8; color: #38bdf8; padding: 3px 8px; border-radius: 4px; font-size: 10px; cursor: pointer; font-weight: 700;">100% Max</button>
              </div>
            </div>

            <div>
              <label style="display: block; font-size: 11px; color: #94a3b8; margin-bottom: 4px; font-weight: 600;">Settlement Rail &amp; Destination Address</label>
              <div style="display: flex; gap: 8px;">
                <select id="payout-network" style="background: #0b0e14; border: 1px solid #334155; border-radius: 6px; padding: 10px; color: #fff; font-size: 12px; width: 140px;">
                  <option value="USDT_TRC20">USDT (TRC-20)</option>
                  <option value="USDT_BEP20">USDT (BEP-20)</option>
                  <option value="BANK_WIRE">Bank Wire</option>
                </select>
                <input id="payout-dest" type="text" placeholder="Wallet address (e.g. TX... or 0x...)" value="T9yD14Nj9j7xAB4dbGeiX9h8unkKHxuWwb" style="flex: 1; box-sizing: border-box; padding: 10px 12px; background: #0b0e14; border: 1px solid #334155; border-radius: 6px; color: #fff; font-family: monospace; font-size: 12px;" />
              </div>
            </div>
          </div>

          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div id="payout-feedback" style="font-size: 12px; font-weight: 600;"></div>
            <button id="btn-request-payout" style="background: linear-gradient(135deg, #10b981, #059669); color: #fff; border: none; padding: 10px 22px; border-radius: 6px; cursor: pointer; font-weight: 700; font-size: 13px; box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);">
              Submit Withdrawal Request &rarr;
            </button>
          </div>
        </div>

      </div>
    `;

    this.bindEvents();
  }

  private bindEvents(): void {
    // Close modal
    this.container.querySelector('#close-affiliate-modal')?.addEventListener('click', () => {
      this.container.style.display = 'none';
    });

    // Copy link
    this.container.querySelector('#btn-copy-link')?.addEventListener('click', () => {
      const linkInput = this.container.querySelector('#aff-ref-link') as HTMLInputElement;
      if (linkInput) {
        navigator.clipboard.writeText(linkInput.value);
        const btn = this.container.querySelector('#btn-copy-link') as HTMLButtonElement;
        btn.innerText = '✓ Copied!';
        btn.style.background = '#10b981';
        setTimeout(() => {
          btn.innerText = 'Copy Link';
          btn.style.background = 'linear-gradient(135deg, #0284c7, #2563eb)';
        }, 2000);
      }
    });

    // Pct shortcuts
    this.container.querySelectorAll('.btn-payout-pct').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!this.data) return;
        const pct = parseInt((btn as HTMLElement).dataset.pct || '100', 10);
        const availUSD = this.data.available_commission_cents / 100;
        const amt = ((availUSD * pct) / 100).toFixed(2);
        const amountInput = this.container.querySelector('#payout-amount') as HTMLInputElement;
        if (amountInput) amountInput.value = amt;
      });
    });

    // Request payout
    this.container.querySelector('#btn-request-payout')?.addEventListener('click', async () => {
      const amountInput = this.container.querySelector('#payout-amount') as HTMLInputElement;
      const destInput = this.container.querySelector('#payout-dest') as HTMLInputElement;
      const netSelect = this.container.querySelector('#payout-network') as HTMLSelectElement;
      const feedback = this.container.querySelector('#payout-feedback') as HTMLElement;
      const submitBtn = this.container.querySelector('#btn-request-payout') as HTMLButtonElement;

      const amountCents = Math.round(parseFloat(amountInput.value) * 100);
      if (isNaN(amountCents) || amountCents <= 0) {
        feedback.innerHTML = '<span style="color: #ef4444;">Please enter a valid payout amount.</span>';
        return;
      }
      if (!destInput.value.trim()) {
        feedback.innerHTML = '<span style="color: #ef4444;">Please enter destination address.</span>';
        return;
      }

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.innerText = 'Submitting…';
      }

      try {
        const res = await fetch('/api/v1/affiliate/payout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`,
          },
          body: JSON.stringify({
            amount_cents: amountCents,
            destination_network: netSelect.value || 'USDT_TRC20',
            destination_address: destInput.value.trim(),
            currency: 'USD',
          }),
        });

        const respData = await res.json();
        if (!res.ok) {
          feedback.innerHTML = `<span style="color: #ef4444;">❌ ${respData.message || 'Payout request failed'}</span>`;
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.innerText = 'Submit Withdrawal Request →';
          }
          return;
        }

        feedback.innerHTML = `<span style="color: #10b981;">✅ Payout request submitted! Reference: ${respData.payout_request_id || respData.payout_id || 'QUEUED'}</span>`;
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.innerText = 'Submit Withdrawal Request →';
        }
        await this.loadDashboard();
      } catch (err: any) {
        feedback.innerHTML = `<span style="color: #ef4444;">❌ ${err.message}</span>`;
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.innerText = 'Submit Withdrawal Request →';
        }
      }
    });
  }
}
